import { call, put, takeEvery } from "redux-saga/effects";
import SweetAlert from '../../../components/sweetalert/SweetAlert';
import * as types from './commonActionType';
import { downloadBase64File } from "../../../utils/fileDownloads";
import * as commonServices from './commonServices';
import * as actions from './commonActions';
import { getCookie } from '../../../store/localStorage';
import { replaceHierarchyTermsWithAlias } from '../../../utils/commonUtils';


function* fetchExportToCSVSaga(action) {
    try {
        const {
            moduleName,
            exportAPIURL,
            fileName,
            isPost = false,
            payload = null,
            showLoader = true,
            requestId = null,
        } = action.payload || {};

        if (showLoader) {
            yield put(actions.setShowLoader(true));
        }

        let response;
        if (isPost) {
            response = yield call(commonServices.exportToCSVWithPost, exportAPIURL, payload);
        } else {
            response = yield call(commonServices.exportToCSV, exportAPIURL);
        }

        const base64Data = response?.values || response;
        const userData = getCookie('userData');
        const aliasedFileName = typeof fileName === 'string'
            ? replaceHierarchyTermsWithAlias(fileName, userData)
            : fileName;

        yield call(downloadBase64File, base64Data, moduleName, aliasedFileName);

        yield put(actions.exportToCSVSuccess({ data: base64Data, requestId, showLoader }));

        if (response && response.success) {
            SweetAlert.success({
                title: 'Success!',
                text: response?.resultMessage || 'Successfully exported data.'
            });
        } else {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to export data.'
            });
        }
    } catch (error) {
        console.error("🚨 fetchExportToCSVSaga Error:", error);
        yield put(actions.exportToCSVFailure({ requestId: action?.payload?.requestId || null, showLoader: action?.payload?.showLoader !== false }));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to export data.'
        });
    }
    finally {
        if (action?.payload?.showLoader !== false) {
            yield put(actions.setShowLoader(false));
        }
    }
}

function* fetchDashboardDetailsSaga() {
    try {
        // Call the API - request utility already returns response.data
        const response = yield call(commonServices.getDashboardDetails);
        if (!response) {
            throw new Error('No response received from API');
        }

        // Dispatch success action with the data
        yield put(actions.dashboardDetailsSuccess(response?.values));

    } catch (error) {
        let errorMessage = 'Failed to fetch regions';

        console.error('Region API Error:', error);

        // Dispatch failure action with error
        yield put(actions.dashboardDetailsFailure(errorMessage));
    }
}

function* fetchQgendaTasksSchedulesSaga(action) {
    try {
        // Call the API - request utility already returns response.data
        const response = yield call(commonServices.getQgendaTasksAndSchedules, action.payload);
        if (!response) {
            throw new Error('No response received from API');
        }

        // Dispatch success action with the data
        yield put(actions.qgendaTasksSchedulesSuccess(response?.values));
        yield put(actions.dashboardDetailsRequest());

        // Show success message
        SweetAlert.success({
            title: 'Success!',
            text: response?.resultMessage || 'Encore schedule data refreshed successfully.'
        });

    } catch (error) {
        let errorMessage = 'Failed to fetch Encore tasks and schedules';

        console.error('Encore API Error:', error);

        // Dispatch failure action with error
        yield put(actions.qgendaTasksSchedulesFailure(errorMessage));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: errorMessage
        });
    }
}

function* fetchAllowedMenusSaga() {
    try {
        const response = yield call(commonServices.getAllowedMenus);
        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.setAllowedMenus(response?.values));
    } catch (error) {
        console.error('Allowed Menus API Error:', error);
    }
}

function* fetchAssignedSitesCountSaga() {
    try {
        const response = yield call(commonServices.getAssignedSitesCount);
        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.assignedSitesCountSuccess(response?.values));
    } catch (error) {
        console.error('Assigned Facilities Count API Error:', error);
        yield put(actions.assignedSitesCountFailure());
    }
}

function* fetchUnassignedSitesCountSaga() {
    try {
        const response = yield call(commonServices.getUnassignedSitesCount);
        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.unassignedSitesCountSuccess(response?.values));
    } catch (error) {
        console.error('Unassigned Facilities Count API Error:', error);
        yield put(actions.unassignedSitesCountFailure());
    }
}

function* fetchAssignedRolesWithPracticesSaga() {
    try {
        const response = yield call(commonServices.getAssignedRolesWithPractices);
        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.assignedRolesWithPracticesSuccess(response?.values));
    } catch (error) {
        console.error('Assigned Roles With Practices API Error:', error);
        yield put(actions.assignedRolesWithPracticesFailure());
    }
}

// Watcher saga: spawns new saga tasks on each request
function* commonSaga() {
    yield takeEvery(types.EXPORT_TO_CSV_REQUEST, fetchExportToCSVSaga);
    yield takeEvery(types.DASHBOARD_DETAILS_REQUEST, fetchDashboardDetailsSaga);
    yield takeEvery(types.QGENDA_TASKS_SCHEDULES_REQUEST, fetchQgendaTasksSchedulesSaga);
    yield takeEvery(types.ALLOWED_MENUS_REQUEST, fetchAllowedMenusSaga);
    yield takeEvery(types.ASSIGNED_SITES_COUNT_REQUEST, fetchAssignedSitesCountSaga);
    yield takeEvery(types.UNASSIGNED_SITES_COUNT_REQUEST, fetchUnassignedSitesCountSaga);
    yield takeEvery(types.ASSIGNED_ROLES_WITH_PRACTICES_REQUEST, fetchAssignedRolesWithPracticesSaga);
}

export default commonSaga;